import { Injectable, inject } from '@angular/core';

import { AuthenticationRequest, CryptoService, Tag, Transaction } from '@ap/fido-uaf-frontend-client';
import { ContentTypeId, TokenTypeId } from '@ap/interface-apic-authenticatie-fido';

import { ApTokenService } from '../token.service';

@Injectable()
export class ApDigipasService {
    private readonly tokenService = inject(ApTokenService);

    public static formatDigipasId(keyIdInput: string): string {
        return keyIdInput.replace(/\D/g, '');
    }

    public async getChallenge(
        keyID: string,
        authenticationRequest: AuthenticationRequest,
        salt: Int8Array,
        aaid: TokenTypeId
    ): Promise<string> {
        const fcParams = this.tokenService.getFinalChallengeParameters(authenticationRequest, this.tokenService.getFacetId());
        const signedDataHash = await this.tokenService.getSignedData(fcParams, salt, authenticationRequest.transaction, aaid, keyID);
        const signedData = this.tokenService.getTagBuffer(Tag.TAG_UAFV1_SIGNED_DATA, signedDataHash);
        const hashedSignedData = new Int8Array(await CryptoService.getCrypto().subtle.digest('SHA-256', signedData.buffer as ArrayBuffer));

        return this.isLogin(authenticationRequest)
            ? `0${this.pad(this.mod(hashedSignedData, 100000), 5)}`
            : `${this.mod(hashedSignedData, 900000) + 100000}`;
    }

    private isLogin(request: AuthenticationRequest): boolean {
        return (
            request.transaction.find(
                (transaction: Transaction) => transaction.contentType === ContentTypeId.DVB_DIGIPASACTION && transaction.content === 'login'
            ) !== undefined
        );
    }

    private mod(nr: Int8Array, op: number): number {
        // If input is null, let's just return 0
        if (nr == null) {
            return 0;
        }
        let m = 0;
        // Start with modding the most significant byte, then repeatedly shift
        // left. This way our value never gets larger than an int
        for (const currentNr of nr) {
            m <<= 8;
            m += currentNr & 0xff;
            m %= op;
        }
        return m;
    }

    private pad(nr: number, targetLength: number): string {
        return this.padStart(`${nr}`, targetLength, '0');
    }

    // https://github.com/uxitten/polyfill/blob/master/string.polyfill.js
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padStart
    private padStart(stringToPad: string, targetLength: number, padString: string): string {
        // truncate if number, or convert non-number to 0;
        let targetLengthBit = targetLength >> 0;
        let padStringValue = String(typeof padString !== 'undefined' ? padString : ' ');
        if (stringToPad.length >= targetLengthBit) {
            return String(stringToPad);
        }
        targetLengthBit = targetLengthBit - stringToPad.length;
        if (targetLengthBit > padString.length) {
            padStringValue += padStringValue.repeat(targetLengthBit / padStringValue.length); // append to original to ensure we are longer than needed
        }
        return padStringValue.slice(0, targetLengthBit) + String(stringToPad);
    }
}
